import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, ElementRef, inject, signal, viewChild, WritableSignal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import {
  ButtonComponent,
  InputComponent,
  MbsSize,
  ModalComponent,
  ModalService,
  ModalSettings,
  SharedPersistentStateEnum,
  TableCellDirective,
  TableControlDirective,
  TableFilterDirective,
  TableGridComponent,
  TableHeader,
  TableNavComponent,
  TextEllipsisComponent,
  ToastService
} from 'mbs-ui-kit';
import { BackupDateTimeFormatComponent } from 'mbs-ui-shared@components/date-time-format';
import { BackupStorageService } from 'mbs-ui-shared@components/sidepanel-backup/tabs/backup-storage/service/backup-storage.service';
import { DeepSyncCodes } from 'mbs-ui-shared@constants/errorCodes';
import { unidentifiedErrorText } from 'mbs-ui-shared@interceptors/error-handler.interceptor';
import { DeepSyncEnum, ItemTypeEnum, MyTreeElements } from 'mbs-ui-shared@models/backup/storages-type';
import Computer, { AgentType } from 'mbs-ui-shared@models/Computer';
import { PasswordModalComponent, PasswordModalParams } from 'mbs-ui-shared@modules/password-modal/password-modal.component';
import { cobbledDateFormat } from 'mbs-ui-shared@utils/date';
import moment from 'moment';
import { debounceTime, first } from 'rxjs/operators';
import { IdsForCurrentFormats } from 'wizards/models/backup-to-restore-models';
import { RestorePointItem } from 'wizards/models/restore-point-models';
import { FilterRemoteCommand, GetBackupContentParams, ParamsForDeepSync, WizardStepsService } from 'wizards/services/wizard-steps.service';

export type SearchModalData = {
  computer: Computer;
  force: boolean;
  searchStr: string;
  showFileVersions: boolean;
  useHistory?: boolean;
  treeElement: MyTreeElements;
};

export type SearchModalRestoreResult = {
  selectedItems: MyTreeElements[];
  treeElement: MyTreeElements;
};

const SearchTableClass = 'd-flex justify-content-start';
const LoadItemsLimit = 300;

/**
 * Author: Roman Sh.
 * Modal for show search result from storage tab
 * */
@UntilDestroy()
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormsModule,
    ButtonComponent,
    NgClass,
    NgTemplateOutlet,
    ModalComponent,
    I18NextPipe,
    InputComponent,
    TableCellDirective,
    TableControlDirective,
    TableFilterDirective,
    TableNavComponent,
    TableGridComponent,
    TextEllipsisComponent,
    BackupDateTimeFormatComponent
  ],
  selector: 'mbs-search-result-modal',
  templateUrl: './search-result-modal.component.html',
  styleUrls: ['./search-result-modal.component.scss']
})
export class SearchResultModalComponent {
  private backupStorageService = inject(BackupStorageService);
  private i18nPipe = inject(I18NextPipe);
  private modalService = inject(ModalService);
  private stepService = inject(WizardStepsService);
  private toastService = inject(ToastService);

  public breadcrumbsBox = viewChild<ElementRef<HTMLDivElement>>('breadcrumbsBox');
  public baseModal = viewChild(ModalComponent);

  public readonly mbsSize = MbsSize;
  public readonly breadcrumbDivider = 'ico ico-arrRight';
  public readonly viewMode: SharedPersistentStateEnum = SharedPersistentStateEnum.table;
  public readonly headers: TableHeader[] = [
    {
      name: this.getTextFromI18Pipe('name'),
      headerName: 'label',
      gridColSize: '5fr',
      class: SearchTableClass,
      overflow: true
    },
    { name: this.getTextFromI18Pipe('path'), headerName: 'path', gridColSize: '5fr', class: SearchTableClass },
    {
      name: this.getTextFromI18Pipe('modifyDate'),
      headerName: 'modifyDateUTC',
      gridColSize: '2fr',
      gridColMin: '160px',
      class: SearchTableClass
    },
    {
      name: this.getTextFromI18Pipe('originalSize'),
      headerName: 'originalSize',
      gridColSize: '1fr',
      gridColMin: '100px',
      class: SearchTableClass,
      overflow: true
    }
  ];
  private readonly pointTypes = [ItemTypeEnum.RestorePoint, ItemTypeEnum.RestorePointIncremental, ItemTypeEnum.RestorePointTLog];
  private readonly uniqNamesSettings: ModalSettings = {
    header: { title: this.i18nPipe.transform('shared:sidepanel-backup.searchSelectedNotUniqTitle') },
    footer: { okButton: { show: false }, cancelButton: { text: this.i18nPipe.transform('shared:buttons.close') } }
  };

  public allowedRecovery: WritableSignal<boolean> = signal<boolean>(false);
  public breadcrumbs: WritableSignal<MyTreeElements[]> = signal<MyTreeElements[]>([]);
  public breadcrumbsHeight: WritableSignal<number> = signal<number>(27);
  public computer: WritableSignal<Computer> = signal<Computer>(null);
  public disableLoadMore: WritableSignal<boolean> = signal<boolean>(false);
  public loading: WritableSignal<boolean> = signal<boolean>(true);
  public loadingMore: WritableSignal<boolean> = signal<boolean>(false);
  public loadingRefresh: WritableSignal<boolean> = signal<boolean>(false);
  public initialSearchString: WritableSignal<string> = signal<string>('');
  public password: WritableSignal<string> = signal<string>('');
  public searchString: WritableSignal<string> = signal<string>('');
  public showFileVersions: WritableSignal<boolean> = signal<boolean>(false);
  public treeElement: WritableSignal<MyTreeElements> = signal<MyTreeElements>(null);
  public useHistory: WritableSignal<boolean> = signal<boolean>(false);

  // Table Data Start
  public searchTableData: WritableSignal<MyTreeElements[]> = signal<MyTreeElements[]>([]);
  public selectedItemsForQR: WritableSignal<MyTreeElements[]> = signal<MyTreeElements[]>([]);
  // Table Data End

  public disabledQRButton = computed(() => !this.selectedItemsForQR()?.length);
  public maxHeightTable = computed(() => {
    const breadCrumbsHeight = this.breadcrumbsHeight();

    return `calc(100dvh - ${269 + breadCrumbsHeight}px)`;
  });

  private bunch: MyTreeElements;
  private passwordModalIsOpen = false;

  constructor() {
    this.initSubscribes();
  }

  public downloadClickHandler(): void {
    const result: SearchModalRestoreResult = {
      selectedItems: this.selectedItemsForQR(),
      treeElement: this.treeElement()
    };

    this.baseModal().save(result);
  }

  public loadMoreClickHandler(): void {
    this.loadTableData(this.treeElement(), this.computer().hid, true);
  }

  public navigateClickHandler(value): void {
    const newValue = { ...value, children: [] };

    this.treeElement.set(newValue);
    this.loadingRefresh.set(true);
    this.loadTableData(newValue, this.computer().hid);
  }

  public searchClickHandler(): void {
    if (!this.searchString()) return;

    this.loadingRefresh.set(true);
    this.loadTableData(this.treeElement(), this.computer().hid);
  }

  public selectedItemsForQRChangeHandler(elements: MyTreeElements[]): void {
    if (this.loading()) return;

    const oldSelected = [...this.selectedItemsForQR()];
    const isOneNewSelect = elements?.length - oldSelected?.length === 1;
    const labelMap: { [key: string]: boolean } = {};
    const uniqLabelItems = [];

    elements.forEach((el) => {
      const label = el.label as string;

      if (!labelMap[label]) uniqLabelItems.push(el);

      labelMap[label] = true;
    });

    if (isOneNewSelect && elements?.length > uniqLabelItems?.length) {
      this.selectedItemsForQR.set(oldSelected);
      this.backupStorageService.selectedItems.set(oldSelected);
      this.showUniqFilesFoldersNamesModal();
    }

    this.selectedItemsForQR.set(uniqLabelItems);
    this.backupStorageService.selectedItems.set(uniqLabelItems);
  }

  public showUniqFilesFoldersNamesModal(): void {
    this.modalService.open(this.uniqNamesSettings, this.i18nPipe.transform('shared:sidepanel-backup.searchSelectedNotUniqText'));
  }

  public getDiskSizeFromKB(bytes: number): string {
    return this.backupStorageService.getDiskSizeFromKB(bytes, this.i18nPipe);
  }

  // Private section
  private initSubscribes() {
    this.allowedRecovery.set(this.backupStorageService.allowedRecovery());

    toObservable(this.baseModal)
      .pipe(
        first((value) => !!value?.data),
        untilDestroyed(this)
      )
      .subscribe({ next: (value) => this.initBaseData(value.data) });

    toObservable(this.searchString)
      .pipe(untilDestroyed(this))
      .subscribe({ next: (str) => this.backupStorageService.searchString.set(str) });
  }

  private initBaseData(data: SearchModalData) {
    this.computer.set(data.computer);
    this.initialSearchString.set(data.searchStr);
    this.searchString.set(data.searchStr);
    this.showFileVersions.set(data.showFileVersions);
    this.treeElement.set(data.treeElement);
    this.useHistory.set(data.useHistory);
    this.bunch = this.backupStorageService.getBunchItem(data.treeElement);
    this.password.set(this.backupStorageService.password()?.[this.bunch?.id] || '');

    this.initBreadcrumbs(data.treeElement);

    data.useHistory ? this.loadSavedData() : this.loadTableData(data.treeElement, data.computer.hid);
  }

  private loadSavedData(): void {
    const searchResult = this.backupStorageService.searchResult();
    this.searchTableData.set(searchResult);
    this.disableLoadMore.set(searchResult?.length < LoadItemsLimit);
    this.searchString.set(this.backupStorageService.searchString());
    this.selectedItemsForQR.set(this.backupStorageService.selectedItems());
    this.loading.set(false);
    this.loadingRefresh.set(false);
  }

  private loadTableData(treeElement: MyTreeElements, hid: string, needLoadMore = false, password = ''): void {
    this.loadingMore.set(!!needLoadMore);
    const loadedData = needLoadMore ? this.searchTableData() : [];
    const requestParams: GetBackupContentParams = this.getParams(treeElement, needLoadMore ? loadedData?.length : 0);

    if (password) requestParams.params.Password = password;

    this.stepService
      .getRemoteCommandData(requestParams, hid)
      .pipe(first(), untilDestroyed(this))
      .subscribe({
        next: (result) => {
          if (!result?.data?.items) return;

          const itemsCount = result.data.items.length || 0;
          const root: MyTreeElements = this.treeElement();
          const storageId = root?.storageId;
          const newTreeItems = needLoadMore
            ? [...loadedData, ...this.getNewTreeItemsFromBackupContent(result.data, root, storageId)]
            : this.getNewTreeItemsFromBackupContent(result.data, root, storageId);

          this.disableLoadMore.set(itemsCount < LoadItemsLimit);
          this.searchTableData.set(newTreeItems);
          this.backupStorageService.searchResult.set(newTreeItems);

          this.loading.set(false);
          this.loadingRefresh.set(false);
          this.loadingMore.set(false);

          if (!needLoadMore) {
            setTimeout(() => this.breadcrumbsHeight.set(this.breadcrumbsBox()?.nativeElement?.offsetHeight || 27), 20);
          }
        },
        error: (e) => this.errorHandler(treeElement, e)
      });
  }

  private showPasswordModal(treeElement: MyTreeElements, fromSync = false): void {
    if (!treeElement || this.passwordModalIsOpen) return;

    this.modalService
      .openCustom(PasswordModalComponent, { data: this.getPasswordModalParams(treeElement) })
      .then((result: { password: FormControl }) => this.processPasswordModalResponse(result?.password?.value, treeElement, fromSync))
      .catch(() => this.offAllLoadings())
      .finally(() => (this.passwordModalIsOpen = false));

    this.passwordModalIsOpen = true;
  }

  private processPasswordModalResponse(password: string, treeElement: MyTreeElements, fromSync = false): void {
    if (this.bunch.id) this.backupStorageService.password.update((passMap) => ({ ...passMap, [this.bunch.id]: password }));

    if (fromSync) return this.getDeepSyncStatus(treeElement, password);

    this.loadTableData(treeElement, this.computer().hid, false, password);
  }

  private getDeepSyncStatus(root: MyTreeElements, password = '', syncDate = { progress: 0, deepSync: DeepSyncEnum.InProgress }): void {
    root.deepSync = syncDate.deepSync;
    root.deepSyncProgress = syncDate.progress;

    if (syncDate.deepSync === DeepSyncEnum.InvalidPassword || syncDate.deepSync === DeepSyncEnum.NeedPassword) {
      return void this.showPasswordModal(root, true);
    }

    if (syncDate.deepSync !== DeepSyncEnum.InProgressColdStorage && syncDate.deepSync !== DeepSyncEnum.InProgress) {
      return void this.loadTableData(root, this.computer().hid, false, password);
    }

    const getDeepSyncStatusParams = this.getDeepSyncParams(root, password || '');

    this.stepService
      .getRemoteCommandData(getDeepSyncStatusParams, this.computer().hid)
      .pipe(first(), debounceTime(800))
      .subscribe({
        next: (res) => {
          if (!res?.data) return void this.offAllLoadings();

          const syncDataFromRes = { deepSync: DeepSyncEnum[res.data.state as string], progress: res.data.progress };

          if (syncDataFromRes.deepSync === DeepSyncEnum.Error) {
            this.toastService.error(res.data.error || res.data.progressMessage || unidentifiedErrorText);
            return void this.offAllLoadings();
          }

          this.getDeepSyncStatus(root, password, syncDataFromRes);
        },
        error: (e) => this.errorHandler(root, e, true)
      });
  }

  private errorHandler(root: MyTreeElements, e: unknown & { error: { errorCode: number } }, fromSync = false): void {
    this.searchTableData.set([]);
    this.backupStorageService.searchResult.set([]);

    const code = +e?.error?.errorCode;

    if (code === 2000 || code === 2046) return this.showPasswordModal(root, fromSync);
    if (DeepSyncCodes.includes(code)) return this.getDeepSyncStatus(root);

    this.offAllLoadings();
  }

  private getNewTreeItemsFromBackupContent(
    data: { items: any[]; isCanBeQuickRestored: boolean; restorePointObjectPath: string },
    root: MyTreeElements,
    storageId: string
  ): MyTreeElements[] {
    if (!data?.items?.length) return [];

    return this.backupStorageService.getNewTreeItemsFromBackupContent(data, root, storageId);
  }

  private initBreadcrumbs(element: MyTreeElements): void {
    if (!element) return;

    let currentItem = element;
    const result: MyTreeElements[] = [];

    while (currentItem?.parent && (currentItem.children?.[0] as MyTreeElements)?.itemType !== ItemTypeEnum.Bunch) {
      result.push(currentItem);
      currentItem = currentItem.parent;
    }

    this.breadcrumbs.set(result.reverse());
  }

  private getTextFromI18Pipe(name: string): string {
    return this.i18nPipe.transform(`shared:sidepanel-backup.backupStorages.browseStorage.tables.${name}`);
  }

  private getParams(item: MyTreeElements, offset = 0): GetBackupContentParams {
    const sortField = 'DisplayName';
    const notBunchOrCBDFile = item.itemType !== ItemTypeEnum.Bunch || item.id === IdsForCurrentFormats.File;

    return {
      agentType: 'backup',
      commandType: 'GetBackupContent',
      params: {
        SessionId: null,
        ConnectionId: null,
        Password: this.password() || '',
        ContentFilter: this.showFileVersions()
          ? FilterRemoteCommand[FilterRemoteCommand.All]
          : FilterRemoteCommand[FilterRemoteCommand.Actual],
        path: item.path,
        SearchPattern: this.searchString(),
        offset: offset || 0,
        limit: LoadItemsLimit,
        order: notBunchOrCBDFile && item.itemType !== ItemTypeEnum.Generation ? `${sortField}Asc` : `${sortField}Desc`
      }
    };
  }

  private getDeepSyncParams(root: MyTreeElements, pass = ''): ParamsForDeepSync {
    const newParams: ParamsForDeepSync = {
      agentType: 'backup',
      commandType: 'GetDeepSyncStatus',
      params: {
        connectionId: root.storageId,
        restoreSourcePrefix: root.prefix,
        bunchId: root.bunchId,
        restorePointDateUtc: root.restorePoint
      }
    };

    if (pass) newParams.params.Password = pass;

    return newParams;
  }

  private getPasswordModalParams(treeElement: MyTreeElements): PasswordModalParams {
    const computer = this.computer();
    const backupVersionUpdated = computer.apps?.find((app) => app.applicationId === AgentType.Backup)?.version?.replaceAll('.', '');

    let bunch: MyTreeElements = treeElement;
    let generation: MyTreeElements;
    let point: MyTreeElements;

    if (bunch.itemType === ItemTypeEnum.Generation) generation = bunch;
    if (this.pointTypes.includes(bunch.itemType)) point = bunch;

    while (
      bunch?.parent &&
      (bunch.children?.[0] as MyTreeElements)?.itemType !== ItemTypeEnum.Bunch &&
      bunch?.itemType !== ItemTypeEnum.Bunch
    ) {
      if (bunch.itemType === ItemTypeEnum.Generation) generation = bunch;
      if (this.pointTypes.includes(bunch.itemType)) point = bunch;
      bunch = bunch.parent;
    }

    if (!generation) {
      generation = (bunch.children as MyTreeElements[]).sort((a, b) => +moment(a.lastRestoreDate).isAfter(b.lastRestoreDate))[0];
    }

    if (!point && generation?.children?.length) {
      point = (generation.children as MyTreeElements[]).sort((a, b) => +moment(a.date).isAfter(b.date))[0];
    }

    if (!point && !generation?.children?.length) {
      const lastDate = moment.utc(generation.lastRestoreDate, 'YYYY-MM-DDTHH:mm:ssZ').utcOffset(0).format(cobbledDateFormat);
      point = {
        path: generation.path + '\\' + lastDate,
        restorePointId: lastDate,
        encryptionPasswordHint: generation.encryptionPasswordHint
      } as unknown as MyTreeElements;
    }

    return {
      password: this.password() || '',
      hid: computer?.hid,
      restorePoint: point ? (point as unknown as RestorePointItem) : null,
      currentEncryptedRestorePoint: point ? (point.label as string) : null,
      backupVersionUpdated: backupVersionUpdated,
      passwordRecoveryEnabled: point && this.allowedRecovery() && !bunch.isLegacy,
      passwordInvalid: false,
      showHint: false,
      collapsing: true
    };
  }

  private offAllLoadings(): void {
    this.loading.set(false);
    this.loadingRefresh.set(false);
    this.loadingMore.set(false);
  }
}
